/**
 * PersonaWorkshopSession - Split-panel workshop orchestrator
 *
 * Left: Guided conversation panel (phases, chips, input)
 * Right: Live persona card preview
 *
 * Quick mode focuses on primary JTBD: Identity → Job → Pains → Outcomes
 */

import { useState, useCallback, useEffect } from 'react'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { usePersonaWorkshop } from '@/hooks/usePersonaWorkshop'
import { PersonaConversationPanel } from './PersonaConversationPanel'
import { PersonaCardPreview } from './PersonaCardPreview'
import type {
  PersonaWorkshopSession as SessionType,
  PersonaData,
  PersonaType,
  PersonaWorkshopPhaseId,
} from '@ispot/shared'

interface Props {
  accountId: string
  projectId: string
  session: SessionType
  onBack: () => void
  /**
   * When set, the wizard is scoped to a specific JTBD:
   * - a number targets an existing jtbd at that index
   * - 'new' tells the wizard to atomically create a fresh JTBD on mount
   * Scoped mode trims phases to goals → pains → outcomes (identity skipped)
   * and shows only the target JTBD in the preview.
   */
  targetJtbd?: number | 'new'
}

const QUICK: PersonaWorkshopPhaseId[] = ['identity', 'goals', 'pains', 'behaviors']
const DEEP: PersonaWorkshopPhaseId[] = ['identity', 'goals', 'pains', 'behaviors', 'voice', 'segment', 'relationships']
const JTBD_SCOPED: PersonaWorkshopPhaseId[] = ['goals', 'pains', 'behaviors']

const PHASE_LABELS: Record<PersonaWorkshopPhaseId, string> = {
  identity: 'Identity',
  goals: 'Job to Be Done',
  pains: 'Key Pains',
  behaviors: 'Desired Outcomes',
  tools: 'Tools',
  voice: 'Voice & Quotes',
  segment: 'Segment',
  relationships: 'Relationships',
}

export function PersonaWorkshopSession({ accountId, projectId, session, onBack, targetJtbd }: Props) {
  const isScoped = targetJtbd !== undefined
  const phases = isScoped ? JTBD_SCOPED : (session.mode === 'deep' ? DEEP : QUICK)
  // In scoped mode, always start at the first JTBD phase — session.currentPhase
  // from the DB tracks the full-persona flow and isn't meaningful here.
  const [currentPhase, setCurrentPhase] = useState(isScoped ? 0 : (session.currentPhase || 0))

  // Initial data, with a fresh blank JTBD appended up front for the 'new' flow
  // so the wizard owns creation atomically (no optimistic state from the parent,
  // no debounce race that could wipe earlier JTBDs).
  const [personaData, setPersonaData] = useState<PersonaData>(() => {
    const base = (session.personaData as PersonaData) || {
      name: '', role: '', archetype: '',
      context: '', decisionPower: '',
      currentTools: [], constraints: [],
      jtbds: [],
    }
    if (targetJtbd === 'new') {
      const fresh = { id: `jtbd-${Date.now()}`, title: '', functional: '', pains: [], gains: [] }
      return { ...base, jtbds: [...(base.jtbds || []), fresh] }
    }
    return base
  })

  // Effective index we edit inside the wizard. For 'new' it's the index of
  // the fresh JTBD we just appended; for numeric it's the caller's index.
  const effectiveJtbdIndex = isScoped
    ? (targetJtbd === 'new' ? (personaData.jtbds?.length || 1) - 1 : (targetJtbd as number))
    : 0

  // Always enter the wizard in edit mode — the completion screen is shown only
  // after the user explicitly finishes the last phase in this session.
  const [isCompleted, setIsCompleted] = useState(false)

  const workshop = usePersonaWorkshop(accountId, projectId)

  // Persist the freshly-appended JTBD exactly once so the DB reflects the new
  // entry even if the user backs out before touching any field.
  useEffect(() => {
    if (targetJtbd === 'new') {
      workshop.updateSession(session.id, { personaData })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const currentPhaseId = phases[currentPhase]

  // Fetch suggestions when phase changes
  useEffect(() => {
    if (session.id && currentPhaseId && currentPhaseId !== 'identity') {
      workshop.fetchSuggestions(session.id, currentPhaseId)
    }
  }, [currentPhase, session.id])

  const handleUpdatePersonaData = useCallback(async (updates: Partial<PersonaData>) => {
    const newData = { ...personaData, ...updates }
    setPersonaData(newData)
    await workshop.updateSession(session.id, {
      currentPhase,
      personaData: newData,
    })
  }, [personaData, currentPhase, session.id, workshop.updateSession])

  const handlePhotoChange = useCallback((photo: string | undefined) => {
    void handleUpdatePersonaData({ photo })
  }, [handleUpdatePersonaData])

  const handleUpdatePersonaType = useCallback(async (personaType: PersonaType) => {
    await workshop.updateSession(session.id, { personaType })
  }, [session.id, workshop.updateSession])

  const handleNext = useCallback(async () => {
    if (currentPhase < phases.length - 1) {
      const nextPhase = currentPhase + 1
      setCurrentPhase(nextPhase)
      await workshop.updateSession(session.id, { currentPhase: nextPhase })
    } else {
      await workshop.completeSession(session.id)
      setIsCompleted(true)
    }
  }, [currentPhase, phases.length, session.id, workshop.updateSession, workshop.completeSession])

  const handleBack = useCallback(async () => {
    if (currentPhase > 0) {
      const prevPhase = currentPhase - 1
      setCurrentPhase(prevPhase)
      await workshop.updateSession(session.id, { currentPhase: prevPhase })
    }
  }, [currentPhase, session.id, workshop.updateSession])

  // Completion screen
  if (isCompleted) {
    const focusJtbd = personaData.jtbds?.[effectiveJtbdIndex]
    const primaryJtbd = focusJtbd
    const painCount = focusJtbd?.pains?.length || 0
    const gainCount = focusJtbd?.gains?.length || 0
    return (
      <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto px-10 py-12">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-market/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-market">
          <Check size={14} /> Persona ready
        </div>
        <h2 className="mb-2 font-display text-[26px] font-semibold tracking-tight">
          {personaData.name || 'Your persona'} is ready
        </h2>
        <p className="mb-10 max-w-[60ch] text-center text-sm text-text-secondary">
          {primaryJtbd?.title
            ? `Primary job: "${primaryJtbd.title}" with ${painCount} pains and ${gainCount} desired outcomes.`
            : 'Your persona has been created.'}
        </p>
        <div className="w-full max-w-[680px]">
          <PersonaCardPreview
            personaData={previewData(personaData, isScoped ? effectiveJtbdIndex : undefined)}
            personaType={session.personaType as PersonaType}
            onPhotoChange={handlePhotoChange}
          />
        </div>
        <button
          onClick={onBack}
          className="mt-10 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-warm-100 hover:text-text"
        >
          <ArrowLeft size={14} /> Back to personas
        </button>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-border-subtle px-7 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-warm-100 hover:text-text"
            title="Back to personas"
          >
            <ArrowLeft size={16} />
          </button>
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
            Persona discovery
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-medium text-text-secondary tabular-nums">
            {PHASE_LABELS[currentPhaseId]}
          </span>
          <span className="h-1 w-1 rounded-full bg-warm-300" />
          <div className="flex items-center gap-1">
            {phases.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === currentPhase ? 'w-6 bg-market' : i < currentPhase ? 'w-3 bg-market/50' : 'w-3 bg-warm-200'
                }`}
              />
            ))}
          </div>
          <span className="text-[11px] text-text-muted tabular-nums">
            {currentPhase + 1} / {phases.length}
          </span>
        </div>
      </div>

      {/* Split Panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Guided Panel */}
        <div className="flex flex-[0_0_58%] flex-col overflow-hidden border-r border-border-subtle">
          <div className="flex-1 overflow-y-auto px-9 py-8">
            <PersonaConversationPanel
              phaseId={currentPhaseId}
              phaseIndex={currentPhase}
              phaseLabel={PHASE_LABELS[currentPhaseId]}
              personaType={session.personaType as PersonaType}
              personaData={personaData}
              suggestions={workshop.suggestions}
              archetypes={workshop.archetypes}
              demographics={workshop.demographics}
              quotes={workshop.quotes}
              isSuggestionsLoading={workshop.isSuggestionsLoading}
              targetJtbdIndex={isScoped ? effectiveJtbdIndex : undefined}
              onUpdatePersonaData={handleUpdatePersonaData}
              onUpdatePersonaType={handleUpdatePersonaType}
            />
          </div>
          <div className="flex items-center justify-between border-t border-border-subtle px-9 py-3">
            <button
              onClick={handleBack}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-warm-100 hover:text-text ${
                currentPhase === 0 ? 'invisible' : ''
              }`}
            >
              <ArrowLeft size={14} /> Back
            </button>
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-1.5 rounded-md bg-market px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-market-dark"
            >
              {currentPhase === phases.length - 1 ? 'Complete' : 'Next'}
              {currentPhase < phases.length - 1 ? <ArrowRight size={14} /> : <Check size={14} />}
            </button>
          </div>
        </div>

        {/* Right: Persona Preview */}
        <div className="flex-1 overflow-y-auto bg-bg-secondary px-7 py-8">
          <PersonaCardPreview
            personaData={previewData(personaData, isScoped ? effectiveJtbdIndex : undefined)}
            personaType={session.personaType as PersonaType}
            onPhotoChange={handlePhotoChange}
          />
        </div>
      </div>
    </div>
  )
}

/** When scoped to a target JTBD, surface only that JTBD as the primary in the preview. */
function previewData(data: PersonaData, targetJtbdIndex?: number): PersonaData {
  if (typeof targetJtbdIndex !== 'number') return data
  const target = data.jtbds?.[targetJtbdIndex]
  if (!target) return { ...data, jtbds: [] }
  return { ...data, jtbds: [target] }
}
